import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import {alpha} from '@mui/material/styles';
import CheckIcon from "@mui/icons-material/Check";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

import agent from '../../agent';
import {DELETE_FREAK, FREAK_PAGE_LOADED, LEAVE_FREAK} from '../../constants/actionTypes';
import {getFreakIcon, isFeed} from '../../utils/freakUtils';
import {
  canRequestJoin,
  countJoinRequests,
  FREAK_OWNER_ACTION,
  FREAK_VIEWER_ROLE,
  getFreakViewerRole,
  getOwnerDangerActions,
  getOwnerPrimaryAction,
  getOwnerSecondaryActions,
  pluralize,
} from '../../utils/freakActions';
import {STATUS_CANCELED, STATUS_FINISHED, STATUS_IN_PROGRESS, STATUS_PLANNED} from '../../constants/commonConstants';
import {capitalizeFirstLetter} from "../../utils/commonUtils";
import {DEFAULT_THEME_COLOR} from "../../constants/theme";
import {CHAT_ENABLED} from "../../constants/featureFlags";
import useTranslation from "../../customHooks/translations";

const LIVE_COLOR = '#ff8836';
const DANGER_COLOR = '#c9364d';
const LEAVE = 'leave';
const DESTRUCTIVE = [FREAK_OWNER_ACTION.CANCEL, FREAK_OWNER_ACTION.DELETE, LEAVE];

const ACTION_ICONS = {
  [FREAK_OWNER_ACTION.START]: <PlayCircleOutlineIcon/>,
  [FREAK_OWNER_ACTION.FINISH]: <SportsScoreIcon/>,
  [FREAK_OWNER_ACTION.EDIT]: <EditOutlinedIcon fontSize="small"/>,
  [FREAK_OWNER_ACTION.MOVE]: <SwapHorizIcon fontSize="small"/>,
  [FREAK_OWNER_ACTION.CANCEL]: <EventBusyIcon fontSize="small"/>,
  [FREAK_OWNER_ACTION.DELETE]: <DeleteOutlineIcon fontSize="small"/>,
  [LEAVE]: <ExitToAppIcon fontSize="small"/>,
};

const chipSx = color => ({
  borderRadius: 20,
  borderColor: alpha(color, 0.6),
  color,
  textTransform: 'none',
  fontWeight: 600,
  px: 1.5,
  '&:hover': {borderColor: color, backgroundColor: alpha(color, 0.08)},
  '&:focus': {outline: 'none'},
});

const primarySx = color => ({
  mt: 1.5,
  py: 1.25,
  borderRadius: 3,
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  backgroundColor: color,
  color: 'white',
  boxShadow: `0 6px 18px ${alpha(color, 0.35)}`,
  '&:hover': {backgroundColor: color, filter: 'brightness(1.08)'},
  '&:focus': {outline: 'none'},
});

const StatePanel = ({icon, title, hint, color, testId, children}) => (
  <Box
    data-testid={testId}
    sx={{
      mt: 2,
      p: 2,
      borderRadius: 4,
      border: `1px solid ${alpha(color, 0.45)}`,
      backgroundColor: alpha(color, 0.08),
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{color, display: 'flex', mt: '2px'}}>{icon}</Box>
      <Box sx={{flex: 1}}>
        <Typography sx={{fontWeight: 700, color}}>{title}</Typography>
        {hint ? <Typography variant="body2" sx={{color: 'text.secondary'}}>{hint}</Typography> : null}
      </Box>
    </Stack>
    {children}
  </Box>
);

const FreakActions = props => {
  const translation = useTranslation();
  const t = translation.freakActions;
  const navigate = useNavigate();
  const freak = props.freak;

  const [confirmAction, setConfirmAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isContextModalOpen, setContextModalOpen] = useState(false);
  const [contexts, setContexts] = useState([]);

  const role = getFreakViewerRole(props.currentUser?.username, freak);
  const primaryAction = getOwnerPrimaryAction(freak);
  const secondaryActions = getOwnerSecondaryActions(freak);
  const dangerActions = getOwnerDangerActions(freak);
  const joinRequests = countJoinRequests(freak);

  const reload = async () => {
    props.onFreakReloaded(await Promise.all([
      agent.Freaks.get(freak.slug, props.device),
      agent.Comments.forFreak(freak.slug),
    ]));
  };

  const run = async fn => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      console.error(error);
    } finally {
      setBusy(false);
    }
  };

  const ask = action => {
    setMenuAnchor(null);
    setConfirmAction(action);
  };

  const handleConfirm = () => {
    const action = confirmAction;
    setConfirmAction(null);
    switch (action) {
      case FREAK_OWNER_ACTION.START:
        return run(async () => { await agent.Freaks.start(freak.id); await reload(); });
      case FREAK_OWNER_ACTION.FINISH:
        return run(async () => { await agent.Freaks.finish(freak.id); await reload(); });
      case FREAK_OWNER_ACTION.CANCEL:
        return run(async () => { await agent.Freaks.cancel(freak.id); await reload(); });
      case FREAK_OWNER_ACTION.DELETE:
        return props.onClickDelete(agent.Freaks.del(freak.id));
      case LEAVE: {
        const participant = freak?.participants?.find(p => p.username === props.currentUser.username);
        return props.onClickLeave(agent.Freaks.leave(freak.id, participant?.id));
      }
      default:
        return null;
    }
  };

  const handleJoin = () => run(async () => { await agent.Freaks.join(freak.id); await reload(); });

  const handleOpenContextModal = async () => {
    setContextModalOpen(true);
    if (!contexts.length) {
      const results = await agent.Freaks.availableContexts();
      setContexts(results || []);
    }
  };

  const moveToContext = async context => {
    setContextModalOpen(false);
    if (context === freak.context) {
      return;
    }
    await run(async () => {
      await agent.Freaks.update({slug: freak.slug, context});
      await reload();
    });
  };

  const handleSecondary = action => {
    if (action === FREAK_OWNER_ACTION.EDIT) {
      navigate(`/freak-editor/${freak.slug}`);
    } else if (action === FREAK_OWNER_ACTION.MOVE) {
      handleOpenContextModal();
    }
  };

  const scrollToComments = () =>
    document.getElementById('freak-comments')?.scrollIntoView({behavior: 'smooth', block: 'start'});

  const actionLabel = action => {
    switch (action) {
      case FREAK_OWNER_ACTION.EDIT:
        return translation.buttons.edit;
      case FREAK_OWNER_ACTION.MOVE:
        return translation.buttons.moveToContext;
      case FREAK_OWNER_ACTION.CANCEL:
        return translation.buttons.cancel;
      case FREAK_OWNER_ACTION.DELETE:
        return translation.buttons.delete;
      case LEAVE:
        return translation.buttons.leave;
      default:
        return t[action];
    }
  };

  const ownerHint = () => {
    if (isFeed(freak)) return t.ownerHintFeed;
    switch (freak.status) {
      case STATUS_PLANNED:
        return t.ownerHintPlanned;
      case STATUS_IN_PROGRESS:
        return t.ownerHintInProgress;
      case STATUS_FINISHED:
        return t.ownerHintFinished;
      case STATUS_CANCELED:
        return t.ownerHintCanceled;
      default:
        return '';
    }
  };

  const renderMore = items => (
    items.length
      ? <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<MoreHorizIcon fontSize="small"/>}
          sx={chipSx('#8a8f9c')}
          disabled={busy}
          aria-haspopup="menu"
          onClick={event => setMenuAnchor(event.currentTarget)}
        >
          {t.more}
        </Button>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          {items.map(action => (
            <MenuItem key={action} onClick={() => ask(action)} sx={{color: DANGER_COLOR, gap: 1}}>
              {ACTION_ICONS[action]}
              {actionLabel(action)}
            </MenuItem>
          ))}
        </Menu>
      </>
      : null
  );

  const renderOwnerPanel = () => (
    <StatePanel
      icon={<WorkspacePremiumOutlinedIcon/>}
      title={t.ownerTitle}
      hint={ownerHint()}
      color={DEFAULT_THEME_COLOR}
      testId="freak-owner-panel"
    >
      {
        primaryAction
          ? <Button
            fullWidth
            variant="contained"
            startIcon={busy ? <CircularProgress size={18} sx={{color: 'white'}}/> : ACTION_ICONS[primaryAction]}
            disabled={busy}
            sx={primarySx(primaryAction === FREAK_OWNER_ACTION.FINISH ? LIVE_COLOR : DEFAULT_THEME_COLOR)}
            onClick={() => ask(primaryAction)}
          >
            {t[primaryAction]}
          </Button>
          : null
      }
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{mt: 1.5}}>
        {secondaryActions.map(action => (
          <Button
            key={action}
            variant="outlined"
            size="small"
            startIcon={ACTION_ICONS[action]}
            sx={chipSx(DEFAULT_THEME_COLOR)}
            disabled={busy}
            onClick={() => handleSecondary(action)}
          >
            {actionLabel(action)}
          </Button>
        ))}
        {renderMore(dangerActions)}
      </Stack>
      {
        joinRequests > 0
          ? <Stack direction="row" spacing={0.75} alignItems="center" sx={{mt: 1.5, color: LIVE_COLOR}}>
            <PendingActionsIcon fontSize="small"/>
            <Typography variant="body2">{pluralize(t.joinRequests, joinRequests)}</Typography>
          </Stack>
          : null
      }
    </StatePanel>
  );

  const renderParticipantPanel = () => (
    <StatePanel
      icon={<CelebrationOutlinedIcon/>}
      title={t.participantTitle}
      hint={t.participantHint}
      color={DEFAULT_THEME_COLOR}
      testId="freak-participant-panel"
    >
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{mt: 1.5}}>
        {CHAT_ENABLED && (
          <Button variant="outlined" size="small" startIcon={<ChatOutlinedIcon fontSize="small"/>} sx={chipSx(DEFAULT_THEME_COLOR)} onClick={() => navigate('/messages')}>
            {translation.buttons.chat}
          </Button>
        )}
        <Button variant="outlined" size="small" startIcon={<ForumOutlinedIcon fontSize="small"/>} sx={chipSx(DEFAULT_THEME_COLOR)} onClick={scrollToComments}>
          {t.comments}
        </Button>
        {renderMore([LEAVE])}
      </Stack>
    </StatePanel>
  );

  const renderVisitorPanel = () => {
    switch (role) {
      case FREAK_VIEWER_ROLE.REQUESTED:
        return <StatePanel icon={<HourglassTopIcon/>} title={t.requestedTitle} hint={t.requestedHint} color={LIVE_COLOR} testId="freak-requested-panel"/>;
      case FREAK_VIEWER_ROLE.INVITED:
        return <StatePanel icon={<DraftsOutlinedIcon/>} title={t.invitedTitle} hint={t.invitedHint} color={DEFAULT_THEME_COLOR} testId="freak-invited-panel"/>;
      case FREAK_VIEWER_ROLE.DECLINED:
        return <StatePanel icon={<PersonOffOutlinedIcon/>} title={t.declinedTitle} hint={t.declinedHint} color="#8a8f9c" testId="freak-declined-panel"/>;
      case FREAK_VIEWER_ROLE.GUEST:
        if (canRequestJoin(role, freak)) {
          return (
            <StatePanel icon={<PersonAddIcon/>} title={t.guestTitle} hint={t.guestHint} color={DEFAULT_THEME_COLOR} testId="freak-guest-panel">
              <Button
                fullWidth
                variant="contained"
                startIcon={busy ? <CircularProgress size={18} sx={{color: 'white'}}/> : <PersonAddIcon/>}
                disabled={busy}
                sx={primarySx(DEFAULT_THEME_COLOR)}
                onClick={handleJoin}
              >
                {t.joinCta}
              </Button>
            </StatePanel>
          );
        }
        return freak.status === STATUS_FINISHED || freak.status === STATUS_CANCELED
          ? <StatePanel icon={<LockOutlinedIcon/>} title={t.closedHint} color="#8a8f9c" testId="freak-closed-panel"/>
          : null;
      default:
        return null;
    }
  };

  const confirmColor = DESTRUCTIVE.includes(confirmAction) ? DANGER_COLOR : DEFAULT_THEME_COLOR;

  return (
    <>
      {role === FREAK_VIEWER_ROLE.OWNER ? renderOwnerPanel() : null}
      {role === FREAK_VIEWER_ROLE.PARTICIPANT ? renderParticipantPanel() : null}
      {renderVisitorPanel()}

      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 4}}}>
        {
          confirmAction
            ? <>
              <Box sx={{display: 'flex', justifyContent: 'center', pt: 3, color: confirmColor, '& svg': {fontSize: 40}}}>
                {ACTION_ICONS[confirmAction]}
              </Box>
              <DialogTitle sx={{textAlign: 'center', pb: 1}}>{t.confirm[confirmAction].title}</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{textAlign: 'center'}}>{t.confirm[confirmAction].message}</DialogContentText>
              </DialogContent>
              <DialogActions sx={{px: 3, pb: 2.5, gap: 1}}>
                <Button onClick={() => setConfirmAction(null)} sx={{color: 'text.secondary', textTransform: 'none'}}>
                  {t.confirm.keep}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  sx={{backgroundColor: confirmColor, textTransform: 'none', borderRadius: 2, '&:hover': {backgroundColor: confirmColor}}}
                >
                  {translation.modals.confirmation.confirm}
                </Button>
              </DialogActions>
            </>
            : null
        }
      </Dialog>

      <Dialog open={isContextModalOpen} onClose={() => setContextModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{translation.modals.moveContext.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translation.modals.moveContext.hint}</DialogContentText>
          {
            contexts.length
              ? <List>
                {contexts.map(context => (
                  <ListItemButton key={context} onClick={() => moveToContext(context)}>
                    <ListItemIcon sx={{color: DEFAULT_THEME_COLOR}}>{getFreakIcon(context)}</ListItemIcon>
                    <ListItemText primary={translation.settings[`context${capitalizeFirstLetter(context)}`] || context}/>
                    {context === freak.context ? <CheckIcon sx={{color: DEFAULT_THEME_COLOR}}/> : null}
                  </ListItemButton>
                ))}
              </List>
              : <CircularProgress sx={{display: 'block', mx: 'auto', my: 2, color: DEFAULT_THEME_COLOR}}/>
          }
        </DialogContent>
      </Dialog>
    </>
  );
};

const mapStateToProps = state => ({
  ...state.freak,
  currentUser: state.common.currentUser,
  device: state.common.device,
});

const mapDispatchToProps = dispatch => ({
  onClickDelete: payload =>
    dispatch({type: DELETE_FREAK, payload}),
  onClickLeave: payload =>
    dispatch({type: LEAVE_FREAK, payload}),
  onFreakReloaded: payload =>
    dispatch({type: FREAK_PAGE_LOADED, payload}),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakActions);
